import { useCallback } from 'react'

import type { Temporal } from '@js-temporal/polyfill'

import { useLocalStorage, useCachedMemo } from '@buffer-mono/popcorn'

import { CURRENT_TIME_ZONE } from '~publish/helpers/dateFormatters'
import { getDateRange } from '~publish/helpers/temporal'
import type { WeekAnchor } from '~publish/helpers/temporal'

import type { CalendarDateRangeViewMode } from './useCalendarDateRangeViewMode'
import { useResponsiveViewMode } from './useResponsiveViewMode'
import { useSelectedDate } from './useSelectedDate'

const DEFAULT_TIMEZONE = CURRENT_TIME_ZONE

/**
 * Interface representing the information and functions returned by the useCalendar hook.
 */
interface CalendarInfo {
  selectedDate: Temporal.ZonedDateTime
  viewMode: CalendarDateRangeViewMode
  startDate: Temporal.ZonedDateTime
  endDate: Temporal.ZonedDateTime
  /** Function to navigate to today's date */
  navigateToToday: () => void
  /** Function to navigate to the previous week or month */
  navigateToPrevious: () => void
  /** Function to navigate to the next week or month */
  navigateToNext: () => void
  /** Function to navigate to an arbitrary date */
  navigateToDate: (date: Temporal.ZonedDateTime) => void
  /** Function to toggle between week and month view */
  toggleViewMode: (mode?: CalendarDateRangeViewMode) => void
  /** Whether the calendar should render mobile-specific behavior */
  isMobile: boolean
}

type Options = {
  timestamp?: number
  weekStartsOn?: 0 | 1
  timezone?: string
  weekAnchor?: WeekAnchor
}

export const LAST_VIEW_MODE_LOCAL_STORAGE_KEY = 'calendar-view-mode'
export const DEFAULT_VIEW_MODE = 'week'
/**
 * A custom React hook that provides logic for the calendar component.
 * It manages the selected date, view mode, date range, and navigation functions.
 *
 * @param {Object} options - The options for the calendar.
 * @param {number} [options.timestamp] - Initial timestamp in milliseconds since epoch.
 * @param {0 | 1} [options.weekStart] - Day of the week to start on (0 for Sunday, 1 for Monday; defaults to 0).
 * @returns {CalendarInfo} An object containing calendar state and functions.
 */
export const useCalendar = (options: Options = {}): CalendarInfo => {
  const {
    timestamp,
    weekStartsOn = 0,
    timezone = DEFAULT_TIMEZONE,
    weekAnchor = 'week-start',
  } = {
    ...options,
  }
  const [viewMode = DEFAULT_VIEW_MODE, setViewMode, isMobile] =
    useResponsiveViewMode()
  const [selectedDate, setSelectedDate] = useSelectedDate(timestamp, {
    timezone,
  })

  // Calculate the date range based on the selected date, view mode, and week start
  const dateRange = useCachedMemo(
    () => getDateRange(selectedDate, viewMode, weekStartsOn, weekAnchor),
    (oldRange, newRange) =>
      oldRange.start.equals(newRange.start) &&
      oldRange.end.equals(newRange.end),
    [selectedDate, viewMode, weekStartsOn, weekAnchor],
  )

  const navigateToToday = useCallback((): void => {
    setSelectedDate(undefined) // This will reset the selected date to today's date
  }, [setSelectedDate])

  const navigateToDate = useCallback(
    (date: Temporal.ZonedDateTime): void => {
      setSelectedDate(date)
    },
    [setSelectedDate],
  )

  const navigateToPrevious = useCallback((): void => {
    const duration =
      viewMode === 'three-day'
        ? { days: 3 }
        : viewMode === 'week'
          ? { weeks: 1 }
          : { months: 1 }
    setSelectedDate(selectedDate.subtract(duration))
  }, [selectedDate, viewMode, setSelectedDate])

  const navigateToNext = useCallback((): void => {
    const duration =
      viewMode === 'three-day'
        ? { days: 3 }
        : viewMode === 'week'
          ? { weeks: 1 }
          : { months: 1 }
    setSelectedDate(selectedDate.add(duration))
  }, [selectedDate, viewMode, setSelectedDate])

  const [, setLastViewMode] = useLocalStorage<CalendarDateRangeViewMode>(
    LAST_VIEW_MODE_LOCAL_STORAGE_KEY,
    DEFAULT_VIEW_MODE,
  )

  const toggleViewMode = useCallback(
    (newMode?: CalendarDateRangeViewMode): void => {
      const resolved = newMode || (viewMode === 'week' ? 'month' : 'week')
      setViewMode(resolved)
      setLastViewMode(resolved)
    },
    [viewMode, setLastViewMode, setViewMode],
  )

  return {
    selectedDate,
    viewMode,
    startDate: dateRange.start,
    endDate: dateRange.end,
    navigateToToday,
    navigateToDate,
    navigateToPrevious,
    navigateToNext,
    toggleViewMode,
    isMobile,
  }
}
